import { useRef } from "react";
import { fieldLabel, SIGNATURE_KEY, DATE_SIGNED_KEY, BUSINESS_SIGNATURE_KEY } from "../shared/agreementFields";

// ─── Boxes on a PDF page: where each fill-in field and the signature go ─────
// Boxes are fractions of the page (see cleanBoxes/placeBox in shared/agreementFields.js).
// Drag on the page to draw a box for the chosen field (or click for a standard size); drag a box to move it,
// its corner to resize it, × to remove it.

const DEFAULT_SIZE = { [SIGNATURE_KEY]: [0.32, 0.07], [BUSINESS_SIGNATURE_KEY]: [0.32, 0.07], units_table: [0.8, 0.12], customer_address: [0.45, 0.05], [DATE_SIGNED_KEY]: [0.2, 0.025] };
const sizeFor = key => DEFAULT_SIZE[key] || [0.3, 0.025];
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const isSigning = key => key === SIGNATURE_KEY || key === DATE_SIGNED_KEY || key === BUSINESS_SIGNATURE_KEY;

export const boxColours = key => (isSigning(key)
  ? { background: "rgba(255,236,170,.55)", border: "2px solid #E8B931", color: "#7A5600" }
  : { background: "rgba(26,79,114,.13)", border: "1.5px solid #1A4F72", color: "#0F3A52" });

/** The overlay for one page. boxes: all boxes; onChange(newBoxes). */
export function FieldBoxesOverlay({ page, boxes, onChange, placing }) {
  const layer = useRef(null);
  const action = useRef(null);

  const point = e => {
    const r = layer.current.getBoundingClientRect();
    return [clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1)];
  };

  function start(e, mode, index) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    layer.current.setPointerCapture(e.pointerId);
    const [x, y] = point(e);
    if (mode === "draw") {
      if (!placing) return;
      const next = [...boxes, { key: placing, page, x, y, w: 0, h: 0 }];
      action.current = { mode, index: next.length - 1, x0: x, y0: y };
      onChange(next);
    } else {
      action.current = { mode, index, x0: x, y0: y, box: { ...boxes[index] } };
    }
  }

  function move(e) {
    const a = action.current;
    if (!a) return;
    const [x, y] = point(e);
    const next = boxes.slice();
    const b = { ...next[a.index] };
    if (a.mode === "draw") {
      Object.assign(b, { x: Math.min(a.x0, x), y: Math.min(a.y0, y), w: Math.abs(x - a.x0), h: Math.abs(y - a.y0) });
    } else if (a.mode === "move") {
      b.x = clamp(a.box.x + x - a.x0, 0, 1 - a.box.w);
      b.y = clamp(a.box.y + y - a.y0, 0, 1 - a.box.h);
    } else if (a.mode === "resize") {
      b.w = clamp(a.box.w + x - a.x0, 0.02, 1 - a.box.x);
      b.h = clamp(a.box.h + y - a.y0, 0.012, 1 - a.box.y);
    }
    next[a.index] = b;
    onChange(next);
  }

  function end() {
    const a = action.current;
    action.current = null;
    if (a?.mode !== "draw") return;
    const next = boxes.slice();
    const b = { ...next[a.index] };
    if (b.w < 0.015 || b.h < 0.008) {   // a click, not a drag: standard size, centred on the click
      const [w, h] = sizeFor(b.key);
      b.w = w; b.h = h;
      b.x = clamp(a.x0 - w / 2, 0, 1 - w); b.y = clamp(a.y0 - h / 2, 0, 1 - h);
    }
    next[a.index] = b;
    onChange(next);
  }

  return (
    <div ref={layer} onPointerDown={e => start(e, "draw")} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
      style={{ position: "absolute", inset: 0, cursor: placing ? "crosshair" : "default", touchAction: "none" }}>
      {boxes.map((b, i) => b.page !== page ? null : (
        <div key={i} onPointerDown={e => start(e, "move", i)}
          style={{ position: "absolute", left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`,
            ...boxColours(b.key), borderRadius: 3, cursor: "move", fontSize: 11, fontWeight: 600, lineHeight: 1.15, overflow: "visible", boxSizing: "border-box" }}>
          <span style={{ position: "absolute", left: 3, top: 1, whiteSpace: "nowrap", pointerEvents: "none" }}>{fieldLabel(b.key)}</span>
          <button type="button" title="Remove" onPointerDown={e => e.stopPropagation()} onClick={() => onChange(boxes.filter((_, j) => j !== i))}
            style={{ position: "absolute", right: -9, top: -9, width: 18, height: 18, borderRadius: 9, border: "none", background: "var(--danger)", color: "#fff", fontSize: 12, lineHeight: "18px", padding: 0, cursor: "pointer" }}>×</button>
          <div title="Drag to resize" onPointerDown={e => start(e, "resize", i)}
            style={{ position: "absolute", right: -5, bottom: -5, width: 12, height: 12, background: "#fff", border: "2px solid currentColor", borderRadius: 2, cursor: "nwse-resize" }} />
        </div>
      ))}
    </div>
  );
}

/** Read-only: highlights where the customer signs. */
export function SignHereOverlay({ page, boxes }) {
  return boxes.filter(b => b.page === page).map((b, i) => (
    <div key={i} style={{ position: "absolute", left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`,
      ...boxColours(SIGNATURE_KEY), borderStyle: "dashed", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, pointerEvents: "none" }}>
      ✍️ Sign here
    </div>
  ));
}
